import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { fiqhContent } from '../data/fiqhData';
import './AshmawiyyahPage.css';

const BOOKMARKS_KEY = 'fiqh_bookmarks';
const FONT_SIZE_KEY = 'book_font_size';

const getBookmarks = (): number[] => {
  const stored = localStorage.getItem(BOOKMARKS_KEY);
  return stored ? JSON.parse(stored) : [];
};

const saveBookmarks = (bookmarks: number[]) => {
  localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarks));
};

const getFontOffset = (): number => {
  return parseInt(localStorage.getItem(FONT_SIZE_KEY) || '0', 10);
};

export const FiqhPage: React.FC = () => {
  const { colors } = useTheme();
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [bookmarks, setBookmarks] = useState<number[]>(getBookmarks);
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);
  const [fontOffset, setFontOffset] = useState(getFontOffset);
  const [query, setQuery] = useState('');

  const toggleBookmark = (index: number) => {
    const updated = bookmarks.includes(index)
      ? bookmarks.filter(b => b !== index)
      : [...bookmarks, index];
    saveBookmarks(updated);
    setBookmarks(updated);
  };

  const changeFontSize = (delta: number) => {
    const next = Math.min(Math.max(fontOffset + delta, -4), 8);
    localStorage.setItem(FONT_SIZE_KEY, next.toString());
    setFontOffset(next);
  };

  const chapters = fiqhContent
    .map((item, index) => ({ ...item, index }))
    .filter(item => {
      if (showBookmarksOnly && !bookmarks.includes(item.index)) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return item.title.toLowerCase().includes(q) || item.content.toLowerCase().includes(q);
    });

  if (selectedChapter !== null) {
    const section = fiqhContent[selectedChapter];
    const total = fiqhContent.length;
    const isBookmarked = bookmarks.includes(selectedChapter);

    return (
      <div className="ashmawiyyah-page" style={{ backgroundColor: colors.background, color: colors.text }}>
        <div className="chapter-header">
          <button className="back-btn" onClick={() => setSelectedChapter(null)} style={{ color: colors.tint }}>
            ← Chapters
          </button>
          <div className="header-actions">
            <button className="font-btn" onClick={() => changeFontSize(-2)} title="Decrease font">A-</button>
            <button className="font-btn" onClick={() => changeFontSize(2)} title="Increase font">A+</button>
            <button
              className={`bookmark-btn ${isBookmarked ? 'active' : ''}`}
              onClick={() => toggleBookmark(selectedChapter)}
              title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              {isBookmarked ? '🔖' : '🏷️'}
            </button>
          </div>
        </div>
        <p style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.tint, margin: '0 0 4px' }}>
          Fiqh {section.section}
        </p>
        <h3 style={{ color: colors.text, marginTop: 0 }}>{section.title}</h3>
        <div className="ashmawiyyah-card" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
          <div className="ashmawiyyah-en" style={{ fontSize: `${0.95 + fontOffset * 0.05}rem`, lineHeight: 1.7 }}>
            {section.content}
          </div>
        </div>
        <div className="chapter-nav" style={{ borderColor: colors.border }}>
          <button
            disabled={selectedChapter === 0}
            onClick={() => setSelectedChapter(selectedChapter - 1)}
            style={{ color: colors.tint }}
          >
            ← Previous
          </button>
          <span style={{ color: colors.muted }}>{selectedChapter + 1} / {total}</span>
          <button
            disabled={selectedChapter === total - 1}
            onClick={() => setSelectedChapter(selectedChapter + 1)}
            style={{ color: colors.tint }}
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ashmawiyyah-page" style={{ backgroundColor: colors.background, color: colors.text }}>
      <h2>📘 Fiqh al-Sunnah</h2>
      <p style={{ color: colors.muted }}>Islamic Jurisprudence — Sayyid Sabiq</p>
      <input
        className="search-input"
        placeholder="Search chapters..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        style={{ backgroundColor: colors.cardBackground, borderColor: colors.border, color: colors.text }}
      />
      {bookmarks.length > 0 && (
        <button
          className={`filter-btn ${showBookmarksOnly ? 'active' : ''}`}
          onClick={() => setShowBookmarksOnly(!showBookmarksOnly)}
        >
          🔖 Bookmarks ({bookmarks.length})
        </button>
      )}
      <div className="chapter-list">
        {chapters.map(item => (
          <div
            key={item.index}
            className="chapter-item"
            style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}
            onClick={() => setSelectedChapter(item.index)}
          >
            <span className="chapter-number" style={{ color: colors.tint }}>{item.section}</span>
            <span className="chapter-title" style={{ color: colors.text }}>{item.title}</span>
            {bookmarks.includes(item.index) && (
              <span
                className="chapter-bookmark"
                onClick={(e) => { e.stopPropagation(); toggleBookmark(item.index); }}
              >
                🔖
              </span>
            )}
            <span className="chapter-arrow" style={{ color: colors.muted }}>›</span>
          </div>
        ))}
      </div>
    </div>
  );
};
